import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { getGateSchedule, getPortsList, SaveApiResMessage } from '@/actions';
import PageContainerAB from '@/components/PageContainerAB/pageContainer';
import { TerminalsIcon } from '@/assets';
import { usePusher } from '../../App';
import LoadingV2 from '@/components/Loading';
import List from './List';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerCustom.css';
import IconButton from '@/ctComponents/IconButton';
import CloseButton from '@/ctComponents/closeButton';
import SubmitButton from '@/ctComponents/submitButton';
import CustomBadge from '@/components/CustomBadge';

const formatDate = (date, format) => {
  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursdays',
    'Friday',
    'Saturday',
  ];
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const dayName = dayNames[date.getDay()];
  const monthNumber = String(monthIndex + 1).padStart(2, '0');
  const month = monthNames[monthIndex];
  if (format === 1) {
    return `${year}-${monthNumber}-${day}`;
  } else {
    return { day, dayName, monthNumber, month, year };
  }
};

const getCurrentWeekDates = (dayDate) => {
  const currentDay = (dayDate.getDay() + 6) % 7; // Adjust so Monday is 0 and Sunday is 6
  const firstDayOfWeek = new Date(dayDate);
  firstDayOfWeek.setDate(dayDate.getDate() - currentDay);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(firstDayOfWeek);
    date.setDate(firstDayOfWeek.getDate() + i);
    dates.push(date);
  }
  return dates;
};

const GateSchedule = () => {
  const { channels } = usePusher();
  const [loading, setLoading] = useState(true);
  const [DATATable, setDataTable] = useState({});
  const [PortsList, setPortsList] = useState({});
  const [selectedPort, setSelectedPort] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState(new Date());
  const dateFilterRef = useRef(null);

  useEffect(() => {
    if (channels?.privateChannel) {
      channels.privateChannel.bind('Export', (data) => {
        console.log('Export event:', data);
      });

      return () => {
        channels.privateChannel.unbind('Export');
      };
    }
  }, [channels]);

  // Handle outside click to close date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDatePickerOpen &&
        dateFilterRef.current &&
        !dateFilterRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDatePickerOpen]);

  const getGateScheduleList = useCallback(
    async (portId = null) => {
      setLoading(true);
      try {
        const dateWeekDays = getCurrentWeekDates(selectedDate);
        const firstWeekDay = formatDate(dateWeekDays[0], 1);
        const endWeekDay = formatDate(dateWeekDays[6], 1);

        const res = await getGateSchedule(firstWeekDay, endWeekDay, portId);
        if (res) {
          setDataTable(res.data.schedule);
        }
      } catch (err) {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedDate]
  );

  const getPortsListData = useCallback(async () => {
    try {
      const res = await getPortsList();
      setPortsList(Object.values(res.data.ports).map((ports) => ports));
    } catch (err) {
      SaveApiResMessage({
        title: 'Error',
        message: err,
        mode: 'warning',
      });
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([getGateScheduleList(), getPortsListData()]);
    };

    loadInitialData();
  }, [getGateScheduleList, getPortsListData]);

  useEffect(() => {
    if (selectedPort || selectedDate) {
      getGateScheduleList(selectedPort?.slug);
    }
  }, [selectedPort, selectedDate, getGateScheduleList]);

  const weekDates = useMemo(
    () => getCurrentWeekDates(selectedDate),
    [selectedDate]
  );
  const formattedDates = useMemo(
    () => weekDates.map((date) => formatDate(date)),
    [weekDates]
  );

  const portSelectOptions = useMemo(
    () =>
      Object.keys(PortsList).map((i, index) => ({
        label: PortsList[index].name,
        slug: PortsList[index].slug,
        value: index + 1,
      })),
    [PortsList]
  );

  const getTerminalIcon = useCallback((terminalName) => {
    const iconMap = {
      APM: TerminalsIcon.APM,
      FMS: TerminalsIcon.FMS,
      EVERPORT: TerminalsIcon.EVERPORT,
      LACT: TerminalsIcon.LACT,
      PCT: TerminalsIcon.PCT,
      PST: TerminalsIcon.PST,
      TRAPAC: TerminalsIcon.TRAPAC,
      WBCT: TerminalsIcon.WBCT,
      WCC: TerminalsIcon.WCC,
      YTI: TerminalsIcon.YTI,
      TTI: TerminalsIcon.TTI,
      ITS: TerminalsIcon.ITS,
      LBCT: TerminalsIcon.LBCT,
      MATSON: TerminalsIcon.MATSON,
      'SSA MARINE': TerminalsIcon.SSA,
      'SSA STL': TerminalsIcon.STL,
      PTS: TerminalsIcon.PTS,
    };
    return iconMap[terminalName] || '';
  }, []);

  const getStatusBadge = useCallback((status) => {
    switch (status) {
      case 1:
        return (
          <span className="flex w-45  h-6 items-center justify-center  rounded-full text-10 font-normal bg-success-light text-success">
            Open
          </span>
        );
      case 0:
        return (
          <span className="flex w-45  h-6 items-center justify-center  rounded-full text-10 font-normal bg-error-light text-error-10">
            Closed
          </span>
        );
      case 2:
        return (
          <span className="flex w-45 h-6 items-center justify-center  rounded-full text-10 font-normal bg-yellow-100 text-yellow-800">
            Dual
          </span>
        );
      default:
        return (
          <span className="flex w-45 h-6 text-center justify-center items-center  rounded-full text-10 font-normal " />
        );
    }
  }, []);

  const handleDateFilterClick = useCallback(() => {
    setTempSelectedDate(selectedDate);
    setIsDatePickerOpen(true);
  }, [selectedDate]);

  const handleDatePickerCancel = useCallback(() => {
    setIsDatePickerOpen(false);
  }, []);

  const handleDatePickerSubmit = useCallback(() => {
    setSelectedDate(tempSelectedDate);
    setIsDatePickerOpen(false);
  }, [tempSelectedDate]);

  const handleResetDate = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  const isToday = useCallback((date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  const formatDisplayDate = useCallback((date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }, []);

  return (
    <PageContainerAB title="Gate Schedule" mainPage minWidth={'unset'}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mt-6 mb-8">
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-primary-text m-0">
              Gate Access Schedule
            </h1>
            <p className="text-secondary-text text-lg m-0">
              View the latest updates on terminal gate schedules and
              availability.
            </p>
          </div>

          <div className="flex flex-col items-start sm:items-center justify-between lg:justify-center sm:flex-row gap-2">
            <div className="w-64">
              <CustomSelectEx
                minHeight={40}
                dropdown
                isClearable
                placeholder="Select Port"
                value={selectedPort}
                onChange={setSelectedPort}
                options={portSelectOptions}
              />
            </div>
            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <div className="relative" ref={dateFilterRef}>
                <IconButton
                  textStyle={{
                    color: '#131517',
                  }}
                  text="Date"
                  icon="filter"
                  onClick={handleDateFilterClick}
                  style={{
                    height: '40px',
                  }}
                />

                {/* Date Picker Popup */}
                {isDatePickerOpen && (
                  <div className="absolute w-291 p-3 top-full rounded-t-md rounded-b-xl border-border-tertiary left-0 sm:left-1/2 translate-x-0 sm:transform-center-x shadow-date-picker mt-2 bg-white border border-solid z-999">
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={tempSelectedDate}
                        onChange={(date) => setTempSelectedDate(date)}
                        inline
                        className="border-none gate-schedule-datepicker-calendar-calendar"
                        showPopperArrow={false}
                        calendarClassName="gate-schedule-datepicker-calendar-calendar"
                      />
                    </div>
                    <div className="flex justify-center items-stretch gap-2  border-t border-gray-200">
                      <CloseButton
                        color="#131517"
                        onClick={handleDatePickerCancel}
                        text="Cancel"
                        className="flex-1"
                      />
                      <SubmitButton
                        onClick={handleDatePickerSubmit}
                        text="Submit"
                        className="flex-1"
                      />
                    </div>
                  </div>
                )}
              </div>
              <IconButton
                text="Export XLSX"
                icon="exportXLSXIcon"
                color="#131517"
                style={{
                  height: '40px',
                }}
                onClick={() => {}}
              />
            </div>
          </div>
        </div>
        {selectedDate && !isToday(selectedDate) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm">Filtered by:</span>
            <CustomBadge
              text={formatDisplayDate(selectedDate)}
              onClick={handleResetDate}
            />
          </div>
        )}
        {/* Table Section */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <LoadingV2 />
          </div>
        ) : (
          <List
            DATATable={DATATable}
            formattedDates={formattedDates}
            getTerminalIcon={getTerminalIcon}
            getStatusBadge={getStatusBadge}
          />
        )}
      </div>
    </PageContainerAB>
  );
};

export default GateSchedule;
